import * as React from 'react';
import { ResponsiveContainer, Tooltip } from 'recharts';
import { cn } from '../../lib/utils';

const ChartContext = React.createContext({ config: {} });

export const ChartContainer = React.forwardRef(({
  config = {}, className, children, ...props
}, ref) => {
  const supportsResponsiveCharts = typeof ResizeObserver !== 'undefined';
  return (
    <ChartContext.Provider value={{ config }}>
      <div
        ref={ref}
        className={cn(
          'flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-slate-500 [&_.recharts-cartesian-grid_line]:stroke-slate-200/80 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-slate-300 [&_.recharts-layer]:outline-none [&_.recharts-sector]:outline-none [&_.recharts-surface]:outline-none',
          className
        )}
        {...props}
      >
        {supportsResponsiveCharts
          ? <ResponsiveContainer>{children}</ResponsiveContainer>
          : <span className="sr-only">Chart preview is available in the browser.</span>}
      </div>
    </ChartContext.Provider>
  );
});
ChartContainer.displayName = 'ChartContainer';

const swatchColor = (item, config) => item.color
  || item.payload?.fill
  || item.payload?.stroke
  || config[item.dataKey]?.color;

export const ChartTooltipContent = React.forwardRef(({
  active, payload, label, className, labelFormatter, valueFormatter, hideLabel = false,
}, ref) => {
  const { config } = React.useContext(ChartContext);
  if (!active || !payload?.length) return null;

  return (
    <div ref={ref} className={cn('min-w-[10rem] rounded-lg border border-slate-200 bg-white/95 px-3 py-2 text-xs shadow-xl backdrop-blur', className)}>
      {!hideLabel && label != null && (
        <p className="mb-1.5 font-bold text-slate-900">{labelFormatter ? labelFormatter(label, payload) : label}</p>
      )}
      <div className="space-y-1.5">
        {payload.map((item) => {
          const key = item.dataKey || item.name;
          const itemConfig = config[key] || config[item.name] || {};
          const itemLabel = item.payload?.label || itemConfig.label || item.name || key;
          return (
            <div key={`${key}-${item.value}`} className="flex min-w-0 items-center justify-between gap-5">
              <span className="flex min-w-0 items-center gap-2 text-slate-600">
                <span className="h-2 w-2 shrink-0 rounded-[2px]" style={{ backgroundColor: swatchColor(item, config) }} />
                <span className="truncate">{itemLabel}</span>
              </span>
              <strong className="font-mono tabular-nums text-slate-950">
                {valueFormatter ? valueFormatter(item.value, item) : item.value}
              </strong>
            </div>
          );
        })}
      </div>
    </div>
  );
});
ChartTooltipContent.displayName = 'ChartTooltipContent';

export const ChartTooltip = Tooltip;

export const ChartLegend = ({ items, className }) => (
  <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-600', className)}>
    {items.map((item) => (
      <span key={item.label} className="inline-flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-[2px]" style={{ backgroundColor: item.color }} />
        {item.label}
      </span>
    ))}
  </div>
);
